import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Globe, Lock, Clock } from 'lucide-react';
import {
  createPortfolio,
  updatePortfolio,
  type PortfolioAccessMode,
  type PortfolioHeaderData,
  type PortfolioSummary,
} from '../../api/portfolios';
import { ApiError } from '../../api/client';
import type { SetSummary } from '../../api/setsTypes';

// PortfolioModal — delad create + edit. När `portfolio` prop ges
// pre-fyller vi formuläret och PATCH:ar istället för POST. Slug är
// låst i edit-mode eftersom existerande länkar skulle bryta annars.
//
// Edit-pin: om hasPin=true men user inte ändrar PIN-fältet, skickas
// pin: undefined så befintlig hash behålls. Tom sträng → null = remove PIN.

interface Props {
  workspaceId: string;
  sets: SetSummary[];
  onClose: () => void;
  onSaved: (portfolio: PortfolioSummary) => void;
  /** När angiven → edit-mode. Pre-fyller alla fält och PATCH:ar vid save. */
  portfolio?: PortfolioSummary;
}

export function CreatePortfolioModal({ workspaceId, sets, onClose, onSaved, portfolio }: Props) {
  const isEdit = !!portfolio;
  const modalRef = useRef<HTMLDivElement>(null);
  // Snapshot av initial-värden — används för att avgöra om formuläret är
  // dirty (har osparade ändringar) vid stäng-försök.
  const initialSnapshotRef = useRef<string>('');

  // Pre-fill om edit
  const [name, setName] = useState(portfolio?.name ?? '');
  const [primarySetId, setPrimarySetId] = useState(
    portfolio?.primarySetId ?? sets[0]?.id ?? '',
  );
  const [secondarySetId, setSecondarySetId] = useState<string>(
    portfolio?.secondarySetId ?? '',
  );
  const [accessMode, setAccessMode] = useState<PortfolioAccessMode>(
    portfolio?.accessMode ?? 'public',
  );
  // PIN: i edit-mode rör vi INTE existerande PIN om inte fältet ändras.
  // Vi använder en separat 'pinDirty'-flagga för att veta om vi ska skicka pin.
  const [pin, setPin] = useState('');
  const [pinDirty, setPinDirty] = useState(false);
  const [expiryDays, setExpiryDays] = useState(() => {
    if (portfolio?.accessMode === 'expiring' && portfolio.expiresAt) {
      const daysLeft = Math.ceil(
        (new Date(portfolio.expiresAt).getTime() - Date.now()) / (24 * 3600 * 1000),
      );
      // Snap till närmaste predefined-värde
      if (daysLeft <= 7) return 7;
      if (daysLeft <= 14) return 14;
      if (daysLeft <= 30) return 30;
      if (daysLeft <= 60) return 60;
      return 90;
    }
    return 30;
  });
  const [expiryDirty, setExpiryDirty] = useState(false);
  // Header — pre-fill från portfolio.headerData om edit
  const h = portfolio?.headerData ?? {};
  const [modelName, setModelName] = useState(h.modelName ?? '');
  const [birthday, setBirthday] = useState(h.birthday ?? '');
  const [location, setLocation] = useState(h.location ?? '');
  const [ethnicity, setEthnicity] = useState(h.ethnicity ?? '');
  const [introText, setIntroText] = useState(h.intro ?? '');
  const [contactEmail, setContactEmail] = useState(h.contact?.email ?? '');
  const [contactInstagram, setContactInstagram] = useState(h.contact?.instagram ?? '');
  const [contactPhone, setContactPhone] = useState(h.contact?.phone ?? '');
  // Measurements
  const [measHeight, setMeasHeight] = useState(h.measurements?.height ?? '');
  const [measBust, setMeasBust] = useState(h.measurements?.bust ?? '');
  const [measWaist, setMeasWaist] = useState(h.measurements?.waist ?? '');
  const [measHips, setMeasHips] = useState(h.measurements?.hips ?? '');
  const [measShoeSize, setMeasShoeSize] = useState(h.measurements?.shoeSize ?? '');
  const [measHair, setMeasHair] = useState(h.measurements?.hair ?? '');
  const [measEyes, setMeasEyes] = useState(h.measurements?.eyes ?? '');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Bygger en stabil sträng-representation av all form-state — används för
  // att jämföra "nu" mot "init" och avgöra om något har ändrats.
  function currentSnapshot(): string {
    return JSON.stringify({
      name,
      primarySetId,
      secondarySetId,
      accessMode,
      pin,
      pinDirty,
      expiryDays,
      expiryDirty,
      modelName,
      birthday,
      location,
      ethnicity,
      introText,
      contactEmail,
      contactInstagram,
      contactPhone,
      measHeight,
      measBust,
      measWaist,
      measHips,
      measShoeSize,
      measHair,
      measEyes,
    });
  }

  // Sätt initial snapshot en gång vid mount
  useEffect(() => {
    initialSnapshotRef.current = currentSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Försök stänga — om dirty fråga om bekräftelse, annars stäng direkt
  function attemptClose() {
    const isDirty = currentSnapshot() !== initialSnapshotRef.current;
    if (isDirty) {
      const ok = window.confirm(
        'You have unsaved changes. Discard them and close?',
      );
      if (!ok) return;
    }
    onClose();
  }

  // Esc → attemptClose (samma confirm-logik)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') attemptClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bygger ett headerData-objekt från fält. Tomt objekt → undefined.
  function buildHeaderData(): PortfolioHeaderData | undefined {
    const headerData: PortfolioHeaderData = {};
    if (modelName.trim()) headerData.modelName = modelName.trim();
    if (birthday.trim()) headerData.birthday = birthday.trim();
    if (location.trim()) headerData.location = location.trim();
    if (ethnicity.trim()) headerData.ethnicity = ethnicity.trim();
    if (introText.trim()) headerData.intro = introText.trim();

    const measurements: PortfolioHeaderData['measurements'] = {};
    if (measHeight.trim()) measurements.height = measHeight.trim();
    if (measBust.trim()) measurements.bust = measBust.trim();
    if (measWaist.trim()) measurements.waist = measWaist.trim();
    if (measHips.trim()) measurements.hips = measHips.trim();
    if (measShoeSize.trim()) measurements.shoeSize = measShoeSize.trim();
    if (measHair.trim()) measurements.hair = measHair.trim();
    if (measEyes.trim()) measurements.eyes = measEyes.trim();
    if (Object.keys(measurements).length > 0) headerData.measurements = measurements;

    const contact: PortfolioHeaderData['contact'] = {};
    if (contactEmail.trim()) contact.email = contactEmail.trim();
    if (contactPhone.trim()) contact.phone = contactPhone.trim();
    if (contactInstagram.trim()) contact.instagram = contactInstagram.trim();
    if (Object.keys(contact).length > 0) headerData.contact = contact;

    return Object.keys(headerData).length > 0 ? headerData : undefined;
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const headerData = buildHeaderData();

      if (isEdit && portfolio) {
        // PATCH — skicka bara fält som kan ha ändrats
        const payload: Parameters<typeof updatePortfolio>[1] = {
          name: name.trim(),
          primarySetId,
          secondarySetId: secondarySetId || null,
          headerData: headerData ?? null,
          accessMode,
        };
        // PIN: bara skicka om dirty
        if (pinDirty) {
          if (accessMode === 'password') {
            payload.pin = pin;
          } else {
            payload.pin = null; // ta bort PIN om mode bytts från password
          }
        }
        // Expires: bara skicka om expiry-mode + dirty (eller om mode bytt till/från expiring)
        if (accessMode === 'expiring' && expiryDirty) {
          payload.expiresAt = new Date(
            Date.now() + expiryDays * 24 * 3600 * 1000,
          ).toISOString();
        } else if (accessMode !== 'expiring' && portfolio.expiresAt) {
          payload.expiresAt = null;
        }
        return updatePortfolio(portfolio.id, payload);
      }

      // CREATE
      const payload = {
        name: name.trim(),
        primarySetId,
        secondarySetId: secondarySetId || undefined,
        headerData,
        accessMode,
        pin: accessMode === 'password' ? pin : undefined,
        expiresAt:
          accessMode === 'expiring'
            ? new Date(Date.now() + expiryDays * 24 * 3600 * 1000).toISOString()
            : undefined,
      };
      return createPortfolio(workspaceId, payload);
    },
    onSuccess: (p) => onSaved(p),
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'SLUG_TAKEN') setErrorMsg('A portfolio with that name already exists.');
        else if (err.message) setErrorMsg(err.message);
        else setErrorMsg('Could not save portfolio.');
      } else {
        setErrorMsg('Network error. Please try again.');
      }
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!name.trim()) {
      setErrorMsg('Portfolio name is required');
      return;
    }
    if (!primarySetId) {
      setErrorMsg('Pick a primary set');
      return;
    }
    if (accessMode === 'password') {
      // I edit-mode: om hasPin redan true och user inte rört PIN-fältet,
      // är det OK — backend behåller existerande hash.
      if (isEdit && portfolio?.hasPin && !pinDirty) {
        // OK
      } else if (pin.length < 4 || pin.length > 8) {
        setErrorMsg('PIN must be 4-8 digits');
        return;
      }
    }
    saveMutation.mutate();
  }

  const isPending = saveMutation.isPending;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8">
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        onClick={attemptClose}
      />
      <div
        ref={modalRef}
        className="relative w-full max-w-[560px] max-h-[calc(100vh-4rem)] overflow-y-auto bg-loupe-bg-2 border border-loupe-border-2 rounded-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 px-6 py-5 border-b border-loupe-border bg-loupe-bg-2/95 backdrop-blur flex items-center justify-between">
          <div>
            <div className="text-[10px] text-loupe-text-3 tracking-[0.18em] uppercase mb-1">
              {isEdit ? 'Edit portfolio' : 'New portfolio'}
            </div>
            <div className="font-display text-xl font-medium tracking-tight">
              {isEdit ? portfolio?.name : 'Build a living link'}
            </div>
          </div>
          <button
            type="button"
            onClick={attemptClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text transition-colors flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-5">
          {/* Name */}
          <Field label="Portfolio name" hint="Internal label — not shown publicly">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Agency Submission — Storm Models"
              className="vl-input"
              maxLength={200}
              autoFocus
            />
          </Field>

          {/* Primary set */}
          <Field label="Primary set" hint="The main focus — large frames">
            <select
              value={primarySetId}
              onChange={(e) => setPrimarySetId(e.target.value)}
              className="vl-input"
            >
              {sets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.imageCount} {s.imageCount === 1 ? 'frame' : 'frames'})
                </option>
              ))}
            </select>
          </Field>

          {/* Secondary set (optional) */}
          <Field label="Secondary set (optional)" hint="Smaller frames in a band beneath">
            <select
              value={secondarySetId}
              onChange={(e) => setSecondarySetId(e.target.value)}
              className="vl-input"
            >
              <option value="">— None —</option>
              {sets.filter((s) => s.id !== primarySetId).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.imageCount} {s.imageCount === 1 ? 'frame' : 'frames'})
                </option>
              ))}
            </select>
          </Field>

          {/* Header meta — öppen som default i edit-mode, stängd i create */}
          <details
            className="border border-loupe-border rounded-lg p-4"
            open={isEdit}
          >
            <summary className="cursor-pointer text-sm text-loupe-text-2 font-medium select-none">
              Header info <span className="text-[11px] text-loupe-text-3 ml-1">(optional)</span>
            </summary>
            <div className="mt-4 flex flex-col gap-3">
              <Field label="Model / project name">
                <input
                  type="text"
                  value={modelName}
                  onChange={(e) => setModelName(e.target.value)}
                  placeholder="Michelle Brodén"
                  className="vl-input"
                />
              </Field>

              <div className="grid grid-cols-2 gap-2">
                <Field label="Birthday" hint="Age beräknas automatiskt">
                  <input
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    className="vl-input"
                  />
                </Field>
                <Field label="Location / base">
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Barcelona, Spain"
                    className="vl-input"
                  />
                </Field>
              </div>

              <Field label="Ethnicity / background">
                <input
                  type="text"
                  value={ethnicity}
                  onChange={(e) => setEthnicity(e.target.value)}
                  placeholder="Swedish / Romanian"
                  className="vl-input"
                />
              </Field>

              <Field label="Intro text">
                <textarea
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  placeholder="A quiet line about who you are and what's in this portfolio."
                  rows={2}
                  className="vl-input"
                  maxLength={400}
                />
              </Field>

              {/* Measurements grid */}
              <div>
                <div className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase mb-2">
                  Measurements
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={measHeight}
                    onChange={(e) => setMeasHeight(e.target.value)}
                    placeholder="Height (178 cm)"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measBust}
                    onChange={(e) => setMeasBust(e.target.value)}
                    placeholder="Bust (86)"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measWaist}
                    onChange={(e) => setMeasWaist(e.target.value)}
                    placeholder="Waist (62)"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measHips}
                    onChange={(e) => setMeasHips(e.target.value)}
                    placeholder="Hips (90)"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measShoeSize}
                    onChange={(e) => setMeasShoeSize(e.target.value)}
                    placeholder="Shoe (EU 38)"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measHair}
                    onChange={(e) => setMeasHair(e.target.value)}
                    placeholder="Hair"
                    className="vl-input"
                  />
                  <input
                    type="text"
                    value={measEyes}
                    onChange={(e) => setMeasEyes(e.target.value)}
                    placeholder="Eyes"
                    className="vl-input"
                  />
                </div>
              </div>

              <Field label="Contact email">
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="vl-input"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="+46 70 123 45 67"
                  className="vl-input"
                />
              </Field>
              <Field label="Instagram handle">
                <input
                  type="text"
                  value={contactInstagram}
                  onChange={(e) => setContactInstagram(e.target.value)}
                  placeholder="@mayaortega"
                  className="vl-input"
                />
              </Field>
            </div>
          </details>

          {/* Access mode */}
          <Field label="Access" hint="Who can view this portfolio?">
            <div className="grid grid-cols-3 gap-2">
              <AccessModeButton
                active={accessMode === 'public'}
                onClick={() => setAccessMode('public')}
                icon={<Globe className="w-4 h-4" />}
                label="Public"
              />
              <AccessModeButton
                active={accessMode === 'password'}
                onClick={() => setAccessMode('password')}
                icon={<Lock className="w-4 h-4" />}
                label="PIN"
              />
              <AccessModeButton
                active={accessMode === 'expiring'}
                onClick={() => setAccessMode('expiring')}
                icon={<Clock className="w-4 h-4" />}
                label="Expiring"
              />
            </div>
          </Field>

          {accessMode === 'password' ? (
            <Field
              label="PIN (4-8 digits)"
              hint={
                isEdit && portfolio?.hasPin && !pinDirty
                  ? 'Leave empty to keep existing PIN, or type a new one to replace it.'
                  : 'Recipients will need to enter this to view'
              }
            >
              <input
                type="text"
                inputMode="numeric"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                  setPinDirty(true);
                }}
                placeholder={isEdit && portfolio?.hasPin && !pinDirty ? '••••' : '1234'}
                maxLength={8}
                className="vl-input font-mono tracking-[0.4em]"
              />
            </Field>
          ) : null}

          {accessMode === 'expiring' ? (
            <Field
              label="Expires after"
              hint={
                isEdit && portfolio?.expiresAt && !expiryDirty
                  ? `Currently expires ${new Date(portfolio.expiresAt).toLocaleDateString()}. Change to reset.`
                  : 'Link stops working after this period'
              }
            >
              <select
                value={expiryDays}
                onChange={(e) => {
                  setExpiryDays(Number(e.target.value));
                  setExpiryDirty(true);
                }}
                className="vl-input"
              >
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
            </Field>
          ) : null}

          {/* Error */}
          {errorMsg ? (
            <div className="text-xs text-michelle">{errorMsg}</div>
          ) : null}

          {/* Submit */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-loupe-border -mx-6 px-6 -mb-5 pb-5 mt-2">
            <button
              type="button"
              onClick={attemptClose}
              className="px-4 py-2.5 bg-transparent border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text rounded-full text-xs uppercase tracking-wider min-h-[40px] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim() || !primarySetId}
              className="px-5 py-2.5 bg-white text-black rounded-full text-xs font-semibold uppercase tracking-wider min-h-[40px] hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 transition-transform"
            >
              {isPending
                ? isEdit ? 'Saving…' : 'Creating…'
                : isEdit ? 'Save changes →' : 'Create portfolio →'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .vl-input {
          width: 100%;
          background: var(--loupe-bg-3, #1a1a1a);
          border: 1px solid var(--loupe-border, #1f1f1f);
          border-radius: 8px;
          padding: 10px 12px;
          color: var(--loupe-text, #ededed);
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;
        }
        .vl-input:focus { border-color: #6e6e6e; }
        select.vl-input { appearance: none; cursor: pointer; }
      `}</style>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase">
        {label}
      </span>
      {children}
      {hint ? <span className="text-[11px] text-loupe-text-3">{hint}</span> : null}
    </label>
  );
}

function AccessModeButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex flex-col items-center gap-2 px-3 py-3 rounded-lg border transition-all min-h-[64px]',
        active
          ? 'border-loupe-text-3 bg-loupe-bg-3 text-loupe-text'
          : 'border-loupe-border bg-transparent text-loupe-text-3 hover:text-loupe-text-2 hover:border-loupe-border-2',
      ].join(' ')}
    >
      {icon}
      <span className="text-[11px] uppercase tracking-wider">{label}</span>
    </button>
  );
}
